import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Injectable } from '@angular/core';
import { DayBlockService } from '@app/infrastructure/core/services/api/day-block.service';
import {
  DayBlockDTO,
  DayBlockPayloadDTO,
  IDayBlockDTO,
  IUpdateDayBlockPositionDto,
} from '@app/infrastructure/models/day-block';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  DayPageService,
  PageDTO,
} from '@app/infrastructure/core/services/api/day-page.service';

export class PageState {
  page: PageDTO = null;
  blockStates: BlockState[] = [];

  constructor(page: PageDTO = new PageDTO()) {
    this.page = page;
  }
}

export class BlockState {
  block: IDayBlockDTO = null;
  editMode: boolean = false;

  constructor(dayBlock?: Partial<IDayBlockDTO>, editMode: boolean = false) {
    this.block = new DayBlockDTO(dayBlock);
    this.editMode = editMode;
  }
}
@Injectable({
  providedIn: 'root',
})
export class BuilderFacade {
  private blocks$: BehaviorSubject<BlockState[]> = new BehaviorSubject([]);
  private activePage$: BehaviorSubject<PageState> = new BehaviorSubject(
    new PageState(),
  );
  private pages$: BehaviorSubject<PageState[]> = new BehaviorSubject([]);

  private mode$: Subject<'view' | 'edit'> = new Subject<'view' | 'edit'>();

  public get observableBlocks$(): Observable<BlockState[]> {
    return this.blocks$.asObservable();
  }

  public set mode(value: 'view' | 'edit') {
    this.mode$.next(value);
  }

  public get observeModeChange$(): Observable<'view' | 'edit'> {
    return this.mode$.asObservable();
  }

  public get blocks(): BlockState[] {
    return this.activePage.blockStates;
  }

  public set blocks(blocks: BlockState[]) {
    const page = this.activePage;
    page.blockStates = blocks;
    this.activePage = page;
  }

  public get observableActivePage(): Observable<PageState> {
    return this.activePage$.asObservable();
  }

  public get activePage(): PageState {
    return this.activePage$.getValue();
  }

  public set activePage(page: PageState) {
    this.activePage$.next(page);
  }

  public get pages(): PageState[] {
    return this.pages$.getValue();
  }

  public set pages(pages: PageState[]) {
    this.pages$.next(pages);
  }

  public gradeUnitDayId: number;

  constructor(
    private dayBlockService: DayBlockService,
    private dayPageService: DayPageService,
  ) {}

  public clearPages() {
    this.pages = [];
    this.activePage = new PageState();
  }

  public loadPages(gradeUnitDayId: number) {
    this.gradeUnitDayId = gradeUnitDayId;
    this.dayPageService.getDayPages(gradeUnitDayId).subscribe((pages) => {
      this.pages = pages.map((page) => new PageState(page));
      this.initialize();
    });
  }

  public loadBlocks(gradeUnitDayId: number) {
    this.gradeUnitDayId = gradeUnitDayId;
    this.dayBlockService
      .getDayBlocks(gradeUnitDayId)
      .subscribe((dayBlocks) => {});
  }

  public initialize() {
    const pages = this.pages;
    pages.forEach((pageState) => {
      pageState.blockStates = pageState.page.blocks.map(
        (dayBlock) => new BlockState(dayBlock),
      );
    });
    this.pages = pages;

    if (!this.activePage.page.id) {
      if (this.pages.length <= 0) this.addNewPage(this.gradeUnitDayId);
      else this.activePage = this.pages[0];
    }
  }

  public addNewPage(gradeUnitDayId) {
    this.dayPageService.createDayPage(gradeUnitDayId).subscribe((page) => {
      const index = this.pages.findIndex(
        (p) => p.page.id === this.activePage.page.id,
      );
      const pages = this.pages;
      pages.splice(index + 1, 0, new PageState(page));
      this.pages = pages;
      this.activePage$.next(this.pages[this.pages.length - 1]);
    });
  }

  public deleteActivePage() {
    this.dayPageService
      .deleteDayPage(
        this.activePage.page.gradeUnitDayId,
        this.activePage.page.id,
      )
      .subscribe((page) => {
        const pages = this.pages;
        const index = this.pages.findIndex((p) => p.page.id === page.id);
        pages.splice(index, 1);
        this.pages = pages;
        this.activePage = pages[index - 1] || new PageState();
      });
  }

  public goToPage(pageNumber: string) {
    const page = this.pages.find(
      (pageState) => pageState.page.pageNumber === pageNumber,
    );
    if (page) {
      this.activePage = page;
      // this.initialize(page.blocks);
    }
  }

  public getCurrentPage(): number {
    return (
      this.pages.findIndex(
        (pageState) => pageState.page.id === this.activePage.page.id,
      ) + 1
    );
  }

  public isFirstPage(): boolean {
    return (
      this.pages.findIndex(
        (pageState) => pageState.page.id === this.activePage.page.id,
      ) === 0 || this.pages.length === 0
    );
  }

  public isLastPage(): boolean {
    return (
      this.pages.findIndex(
        (pageState) => pageState.page.id === this.activePage.page.id,
      ) ===
        this.pages.length - 1 || this.pages.length === 0
    );
  }

  public goToNextPage() {
    const index = this.pages.findIndex(
      (pageState) => pageState.page.id === this.activePage.page.id,
    );
    if (index < this.pages.length - 1) {
      const nextPage = this.pages[index + 1];
      this.activePage = nextPage;
    }
  }

  public goToPreviousPage() {
    const index = this.pages.findIndex(
      (pageState) => pageState.page.id === this.activePage.page.id,
    );
    if (index > 0) {
      const prevPage = this.pages[index - 1];
      this.activePage = prevPage;
    }
  }

  public addNewBlock() {
    const page = this.activePage;
    const blocks = page.blockStates;
    this.dayBlockService
      .createBlock(page.page.gradeUnitDayId, {
        pageId: page.page.id,
        position: blocks.length,
        blockType: null,
        gradeUnitDayId: page.page.gradeUnitDayId,
        edReviews: [],
      })
      .subscribe((block) => {
        blocks.push(new BlockState(block));
      });
    this.activePage = page;
  }

  public addBlockAtPosition(index) {
    const page = this.activePage;
    const blocks = page.blockStates;
    const block = new BlockState(
      { pageId: this.activePage.page.id, position: index },
      true,
    );
    blocks.splice(index, 0, block);
    this.activePage = page;
    this.updateBlockPositions();
  }

  public updateSavedBlock(blockState: BlockState) {
    const pageIndex = this.pages.findIndex(
      (pageState) => +pageState.page.id === +blockState.block.pageId,
    );
    if (pageIndex === -1) return;
    const blockIndex = this.pages[pageIndex].blockStates.findIndex(
      (oldBlockState) =>
        +oldBlockState.block.position === +blockState.block.position,
    );
    if (blockIndex === -1) return;
    const pages = this.pages;
    pages[pageIndex].blockStates[blockIndex] = blockState;
    if (this.activePage.page.id === pages[pageIndex].page.id) {
      const page = this.activePage;
      page.blockStates[blockIndex] = blockState;
      this.activePage = page;
    }
    this.pages = pages;
  }

  public removeBlock(index: number) {
    if (this.blocks.length <= 0) return;
    const blocks = this.blocks;
    const removedBlock: BlockState = blocks.splice(index, 1)[0];
    if (removedBlock?.block?.id) {
      this.dayBlockService
        .deleteDayBlock(
          this.activePage.page.gradeUnitDayId,
          removedBlock.block.id,
        )
        .subscribe((dayBlock) => {
          this.blocks = blocks;
          this.activePage.blockStates = blocks;
        });
    } else {
      this.blocks = blocks;
      this.activePage.blockStates = blocks;
    }
  }

  public switchViewState(index: number): BlockState {
    const blocks = this.blocks;
    blocks[index].editMode = !blocks[index].editMode;
    this.blocks = blocks;
    return blocks[index];
  }

  public moveBlockToPage(blockIndex: number, pagePositon: -1 | 1) {
    const pages = this.pages;
    const pageIndex = pages.findIndex(
      (pageState) => pageState.page.id === this.activePage.page.id,
    );
    const newPageIndex = pageIndex + pagePositon;
    if (pageIndex < 0 || newPageIndex < 0) return;

    const currentPage = pages[pageIndex];
    const block: BlockState = currentPage.blockStates.splice(blockIndex, 1)[0];
    this.activePage = currentPage;
    this.pages = pages;

    const newPage = pages[newPageIndex];
    block.block.position = newPage.blockStates.length;
    block.block.pageId = newPage.page.id;
    newPage.blockStates.push(block);

    if (!block.block.id) return;
    this.dayBlockService
      .updateDayBlock(
        this.gradeUnitDayId,
        new DayBlockPayloadDTO(block.block),
        false,
      )
      .subscribe((result) => {
        console.log(result);
      });
  }

  public reorderBlock(prev: number, current: number) {
    if (prev === current) return;

    const blocks = this.blocks;

    if (!blocks[prev].block.id || !blocks[current].block.id)
      throw new Error('Block is not saved!');

    moveItemInArray(this.activePage.blockStates, prev, current);
    moveItemInArray(this.activePage.page.blocks, prev, current);

    this.updateBlockPositions();
  }

  private updateBlockPositions() {
    const blocks = this.blocks;
    blocks.forEach((blockState, index) => {
      blockState.block.position = index;
    });
    const blockPositions: IUpdateDayBlockPositionDto[] = this.blocks
      .filter((blockState) => blockState.block.id)
      .map((blockState) => {
        return { id: blockState.block.id, position: blockState.block.position };
      });

    if (blockPositions.length <= 0) return;

    this.dayBlockService
      .updateDayBlockPositions(
        this.activePage.page.gradeUnitDayId,
        blockPositions,
      )
      .subscribe((dayBlocks) => {
        this.blocks = blocks;
        this.activePage.blockStates = blocks;
      });
  }
}
